import { normalizeLearningLanguageCode } from '../constants/learningLanguages';
import { OFFERED_PLACEMENT_LEVELS } from '../services/placementEngine';

export const DEFAULT_ONBOARDING_STATE = {
  selectedLanguage: null,
  selectedLevel: null,
  levelSource: null,
  testResults: null,
  currentStep: 'language',
  journeySource: 'onboarding',
};

const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

function normalizeCefrLevel(value) {
  const normalizedValue = String(value || '').trim().toUpperCase();
  return CEFR_LEVELS.includes(normalizedValue) ? normalizedValue : null;
}

function normalizeBoolean(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    if (value === 'true') return true;
    if (value === 'false') return false;
  }
  return null;
}

export function mergeOnboardingStateWithJourney(state, journey) {
  const existingState = state && typeof state === 'object'
    ? { ...DEFAULT_ONBOARDING_STATE, ...state }
    : DEFAULT_ONBOARDING_STATE;
  const journeyLanguage = normalizeLearningLanguageCode(journey?.language, null);

  if (!journeyLanguage) return existingState;

  const canResumeExistingStep = existingState.selectedLanguage === journeyLanguage
    && existingState.currentStep
    && existingState.currentStep !== 'language';

  return {
    ...existingState,
    selectedLanguage: journeyLanguage,
    currentStep: canResumeExistingStep ? existingState.currentStep : 'welcome',
    journeySource: journey?.source || existingState.journeySource || 'onboarding',
  };
}

export function resolveOnboardingResumePath(state) {
  const resolvedState = state && typeof state === 'object'
    ? { ...DEFAULT_ONBOARDING_STATE, ...state }
    : DEFAULT_ONBOARDING_STATE;
  const language = normalizeLearningLanguageCode(resolvedState.selectedLanguage, null);
  if (!language) return '/onboarding';

  switch (resolvedState.currentStep) {
    case 'placement_test':
      return `/${language}/onboarding/placement-test`;
    case 'level_select':
      return `/${language}/onboarding/level-select`;
    case 'complete': {
      const level = normalizeCefrLevel(resolvedState.selectedLevel) || 'B1';
      const source = resolvedState.levelSource === 'test' ? 'test' : 'manual';
      return `/${language}/onboarding/complete?level=${level}&source=${source}`;
    }
    case 'welcome':
    default:
      return `/${language}/onboarding/welcome`;
  }
}

export function buildFirstLessonPath(language, episode) {
  const normalizedLanguage = normalizeLearningLanguageCode(language, 'en');
  if (episode?.id) {
    return `/${normalizedLanguage}/lessons/${encodeURIComponent(episode.id)}`;
  }
  return `/${normalizedLanguage}/podcast-lessons`;
}

export function getAvailablePlacementStartingLevels(recommendedLevel) {
  const highestAllowedIndex = OFFERED_PLACEMENT_LEVELS.indexOf(
    normalizeCefrLevel(recommendedLevel) || ''
  );

  if (highestAllowedIndex < 0) {
    return ['A2'];
  }

  return OFFERED_PLACEMENT_LEVELS.slice(0, highestAllowedIndex + 1);
}

export function sanitizePlacementTestResults(testResults, selectedLevel, selectedLanguage = null) {
  if (!testResults || typeof testResults !== 'object') {
    return null;
  }

  const level = normalizeCefrLevel(
    testResults.selectedStartingLevel || testResults.level || selectedLevel
  );
  const recommendedLevel = normalizeCefrLevel(testResults.recommendedLevel || testResults.level || selectedLevel);
  const assessedLevel = normalizeCefrLevel(testResults.assessedLevel || recommendedLevel || level);
  const selectedStartingLevel = normalizeCefrLevel(testResults.selectedStartingLevel || level || recommendedLevel);
  const wasAdjusted = normalizeBoolean(testResults.wasAdjusted);
  const rangeStatus = typeof testResults.rangeStatus === 'string' && testResults.rangeStatus.trim()
    ? testResults.rangeStatus.trim()
    : null;
  const language = normalizeLearningLanguageCode(
    testResults.language ?? selectedLanguage,
    null
  );
  const correct = Number(testResults.correct ?? testResults.correctAnswers);
  const total = Number(testResults.total ?? testResults.totalQuestions);
  const percentage = Number(testResults.percentage);

  const sanitized = {
    level,
  };

  if (recommendedLevel) {
    sanitized.recommendedLevel = recommendedLevel;
  }

  if (assessedLevel) {
    sanitized.assessedLevel = assessedLevel;
  }

  if (selectedStartingLevel) {
    sanitized.selectedStartingLevel = selectedStartingLevel;
  }

  if (typeof wasAdjusted === 'boolean') {
    sanitized.wasAdjusted = wasAdjusted;
  } else if (recommendedLevel && selectedStartingLevel) {
    sanitized.wasAdjusted = recommendedLevel !== selectedStartingLevel;
  }

  if (rangeStatus) {
    sanitized.rangeStatus = rangeStatus;
  }

  if (language) {
    sanitized.language = language;
  }

  if (Number.isFinite(correct)) {
    sanitized.correct = correct;
  }

  if (Number.isFinite(total)) {
    sanitized.total = total;
  }

  if (Number.isFinite(percentage)) {
    sanitized.percentage = percentage;
  }

  const attemptId = typeof testResults.attemptId === 'string' ? testResults.attemptId.trim() : '';
  const testVersion = typeof testResults.testVersion === 'string' ? testResults.testVersion.trim() : '';
  const responses = Array.isArray(testResults.responses)
    ? testResults.responses
      .slice(0, 100)
      .map((response, index) => ({
        questionId: typeof response?.questionId === 'string' ? response.questionId.trim() : '',
        answerId: typeof response?.answerId === 'string' ? response.answerId.trim() : '',
        sequence: index + 1,
      }))
      .filter((response) => response.questionId && response.answerId)
    : [];

  if (/^[A-Za-z0-9_-]{8,80}$/.test(attemptId) && testVersion && responses.length > 0) {
    sanitized.attemptId = attemptId;
    sanitized.testVersion = testVersion;
    sanitized.responses = responses;
  }

  return sanitized.level ? sanitized : null;
}

export function sanitizePlacementDecision(placementDecision, context = {}) {
  const recommendedLevel = normalizeCefrLevel(
    placementDecision?.recommendedLevel
    ?? context?.testResults?.recommendedLevel
    ?? context?.testResults?.level
    ?? context?.selectedLevel
  );
  const assessedLevel = normalizeCefrLevel(
    placementDecision?.assessedLevel
    ?? context?.testResults?.assessedLevel
    ?? recommendedLevel
  );
  const selectedStartingLevel = normalizeCefrLevel(
    placementDecision?.selectedStartingLevel
    ?? context?.testResults?.selectedStartingLevel
    ?? context?.selectedLevel
    ?? recommendedLevel
  );

  if (!recommendedLevel && !selectedStartingLevel) {
    return null;
  }

  const source = typeof placementDecision?.source === 'string' && placementDecision.source.trim()
    ? placementDecision.source.trim()
    : 'post_test_one_time_choice';
  const language = normalizeLearningLanguageCode(
    placementDecision?.language
    ?? context?.testResults?.language
    ?? context?.selectedLanguage,
    null
  );
  const explicitAdjusted = normalizeBoolean(placementDecision?.wasAdjusted);
  const wasAdjusted = typeof explicitAdjusted === 'boolean'
    ? explicitAdjusted
    : Boolean(recommendedLevel && selectedStartingLevel && recommendedLevel !== selectedStartingLevel);

  const sanitized = {
    source,
    recommendedLevel: recommendedLevel || selectedStartingLevel,
    assessedLevel: assessedLevel || recommendedLevel || selectedStartingLevel,
    selectedStartingLevel: selectedStartingLevel || recommendedLevel,
    wasAdjusted,
  };

  if (language) {
    sanitized.language = language;
  }

  return sanitized;
}

export function resolveOnboardingCompletionPayload(onboardingState, overrides = {}) {
  const state = onboardingState && typeof onboardingState === 'object'
    ? onboardingState
    : DEFAULT_ONBOARDING_STATE;

  const selectedLanguage = normalizeLearningLanguageCode(
    overrides.selectedLanguage ?? state.selectedLanguage,
    null
  );
  const selectedLevel = String(
    overrides.selectedLevel
    ?? state.selectedLevel
    ?? ''
  ).trim().toUpperCase() || null;
  const levelSource = overrides.levelSource ?? state.levelSource ?? 'manual';
  const testResults = sanitizePlacementTestResults(
    overrides.testResults ?? state.testResults,
    selectedLevel,
    selectedLanguage
  );
  const placementDecision = sanitizePlacementDecision(overrides.placementDecision, {
    selectedLanguage,
    selectedLevel,
    testResults,
  });

  return {
    selectedLanguage,
    selectedLevel,
    levelSource,
    testResults: levelSource === 'test' ? testResults : null,
    placementDecision: levelSource === 'test' ? placementDecision : null,
  };
}
